import { field, formatUnits, parseUnits, readContract, writeContractAndWait } from "@/helper/viem";
import MafiaJackpotAbi from "./MafiaJackpot.json";
import config from "@/config/config";
import { JackpotAssetType } from "../model/jackpot.enums";
import { JackpotEntry } from "../model/JackpotEntry";
import { approveToken, getTokenAllowance } from "@/helper/contractFunctions";
import { approveGameCash, getGameBankAllowance } from "@/helper/contractFunctions/MafiaGameBank";
import {
  approveHelperCredit,
  getHelperCreditAllowance,
} from "@/helper/contractFunctions/MafiaHelperCredit";
import { isCrateApprovedForAll, setCrateApprovalForAll } from "@/helper/contractFunctions/OgCrate";

const address = config.jackpot;
const abi = MafiaJackpotAbi;

export const getAssetsPrice = async () => {
  const assetsPrice: any = await readContract({
    address,
    abi,
    functionName: "getAssetsPrice",
  });

  return {
    [JackpotAssetType.OG_CRATE]: Number(
      formatUnits(field(assetsPrice, "cratePrice", 3) || "0", 18)
    ),
    [JackpotAssetType.PERK_BOX]: Number(
      formatUnits(field(assetsPrice, "perkBoxPrice", 4) || "0", 18)
    ),
    [JackpotAssetType.MAFIA_TOKEN]: Number(
      formatUnits(field(assetsPrice, "mafiaPrice", 0) || "0", 18)
    ),
    [JackpotAssetType.IN_GAME_CASH]: Number(
      formatUnits(field(assetsPrice, "cashPrice", 1) || "0", 18)
    ),
    [JackpotAssetType.HELPER_CREDIT]: Number(
      formatUnits(field(assetsPrice, "creditPrice", 2) || "0", 18)
    ),
  };
};

export const getBetLimits = async () => {
  const betLimits: any = await readContract({
    address,
    abi,
    functionName: "getBetLimits",
  });

  return {
    minimumBetUSD: Number(formatUnits(field(betLimits, "minBetUSD", 0) || "0", 18)),
    maximumBetUSD: Number(formatUnits(field(betLimits, "maxBetUSD", 1) || "0", 18)),
  };
};

export const setBetLimits = async (
  minimumBetUSD: number,
  maximumBetUSD: number,
  account: string
) => {
  const minBetInWei = parseUnits(minimumBetUSD.toString(), 18);
  const maxBetInWei = parseUnits(maximumBetUSD.toString(), 18);

  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "setBetLimits",
    args: [minBetInWei, maxBetInWei],
    account,
  });

  return data;
};

export const approveJackpotAsset = async (
  asset: JackpotAssetType,
  amount: number,
  account: string
) => {
  let tokenAddress = "";
  let spender = config.jackpot;

  switch (asset) {
    case JackpotAssetType.OG_CRATE:
      tokenAddress = config.ogCrateAddress;
      break;
    case JackpotAssetType.MAFIA_TOKEN:
      tokenAddress = config.mafiaAddress;
      break;
    case JackpotAssetType.IN_GAME_CASH:
      tokenAddress = config.gamebankAddress;
      break;
    case JackpotAssetType.HELPER_CREDIT:
      tokenAddress = config.helperCreditAddress;
      break;
    case JackpotAssetType.PERK_BOX:
      // Perk boxes don't need approval
      return;
    default:
      throw new Error("Invalid asset type");
  }

  if (tokenAddress) {
    if (asset === JackpotAssetType.OG_CRATE) {
      const isApproved = await isCrateApprovedForAll(account, spender);
      if (!isApproved) {
        await setCrateApprovalForAll(spender, true, account);
      }
    } else if (asset === JackpotAssetType.IN_GAME_CASH) {
      const allowance = await getGameBankAllowance(account, spender);
      if (Number(allowance) < Number(amount)) {
        await approveGameCash(10 ** 18, spender, account);
      }
    } else if (asset === JackpotAssetType.HELPER_CREDIT) {
      const allowance = await getHelperCreditAllowance(account, spender);
      if (Number(allowance) < Number(amount)) {
        await approveHelperCredit(10 ** 18, spender, account);
      }
    } else {
      const allowance = await getTokenAllowance(tokenAddress, account, spender);
      if (Number(allowance) < Number(amount)) {
        await approveToken(tokenAddress, Number(amount), spender, account);
      }
    }
  }
};

export const enterPot = async (
  entryType: JackpotAssetType,
  amount: number,
  account: string
) => {
  let amountInWei: string | bigint = "0";
  if (entryType === JackpotAssetType.OG_CRATE) {
    amountInWei = amount.toString();
  } else {
    amountInWei = parseUnits(amount.toString(), 18);
  }

  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "enterPot",
    args: [entryType, amountInWei],
    account,
  });

  return data;
};

export const enterPotWithPerkBoxes = async (
  perkBoxItemIds: number[],
  account: string
) => {
  const data = await writeContractAndWait({
    address,
    abi,
    functionName: "enterPotWithPerkBoxes",
    args: [perkBoxItemIds],
    account,
  });

  return data;
};

export const getTotalAccumulatedFees = async () => {
  const totalAccumulatedFees: any = await readContract({
    address,
    abi,
    functionName: "getTotalAccumulatedFees",
  });
  return {
    accumulatedMafiaFees: Number(
      formatUnits(totalAccumulatedFees[0] || "0", 18)
    ),
    accumulatedCashFees: Number(
      formatUnits(totalAccumulatedFees[1] || "0", 18)
    ),
    accumulatedCreditFees: Number(
      formatUnits(totalAccumulatedFees[2] || "0", 18)
    ),
  };
};

export const getTotalFeeUSD = async () => {
  const totalFeeUSD: any = await readContract({
    address,
    abi,
    functionName: "getTotalFeeUSD",
  });
  return Number(formatUnits(totalFeeUSD || "0", 18));
};

export const getCurrentRoundTotalPotUSD = async () => {
  const currentRoundTotalPotUSD: any = await readContract({
    address,
    abi,
    functionName: "getCurrentRoundTotalPotUSD",
  });
  return Number(formatUnits(currentRoundTotalPotUSD || "0", 18));
};

export const getCurrentRoundFeeAmount = async () => {
  const currentRoundFeeAmount: any = await readContract({
    address,
    abi,
    functionName: "getCurrentRoundFeeAmount",
  });
  return Number(formatUnits(currentRoundFeeAmount || "0", 18));
};

export const getCurrentRoundPerkBoxItemIds = async () => {
  const currentRoundPerkBoxItemIds: any = await readContract({
    address,
    abi,
    functionName: "getCurrentRoundPerkBoxItemIds",
  });
  return currentRoundPerkBoxItemIds.map((item: any) => Number(item));
};

export const getEntryDetails = async (entryId: number) => {
  const entryDetails: any = await readContract({
    address,
    abi,
    functionName: "getEntryDetails",
    args: [entryId],
  });
  return {
    player: entryDetails[0],
    entryId: entryDetails[1],
    entryType: entryDetails[2] as JackpotAssetType,
    amount: Number(formatUnits(entryDetails[3] || "0", 18)),
    usdAmount: Number(formatUnits(entryDetails[4] || "0", 18)),
    timestamp: Number(entryDetails[5]),
    perkBoxItemIds: entryDetails[6].map((item: any) => Number(item)),
  } as JackpotEntry;
};

export const getPlayerEntries = async (player: string) => {
  const playerEntries: any = await readContract({
    address,
    abi,
    functionName: "getPlayerEntries",
    args: [player],
  });
  return playerEntries.map(
    (entry: any) =>
      ({
        player: entry[0],
        entryId: entry[1],
        entryType: entry[2] as JackpotAssetType,
        amount: Number(formatUnits(entry[3] || "0", 18)),
        usdAmount: Number(formatUnits(entry[4] || "0", 18)),
        timestamp: Number(entry[5]),
        perkBoxItemIds: entry[6].map((item: any) => Number(item)),
      } as JackpotEntry)
  );
};

export const getRoundAmounts = async (roundId: number) => {
  const roundAmounts: any = await readContract({
    address,
    abi,
    functionName: "getRoundAmounts",
    args: [roundId],
  });
  return {
    mafiaAmount: Number(formatUnits(roundAmounts[0] || "0", 18)),
    inGameCashAmount: Number(formatUnits(roundAmounts[1] || "0", 18)),
    helperCreditAmount: Number(formatUnits(roundAmounts[2] || "0", 18)),
    perkBoxAmount: Number(formatUnits(roundAmounts[3] || "0", 18)),
    ogCrateAmount: Number(formatUnits(roundAmounts[4] || "0", 18)),
  };
};

export const getCurrentRound = async () => {
  const currentRound: any = await readContract({
    address,
    abi,
    functionName: "getCurrentRound",
  });
  return {
    roundId: Number(currentRound[0]),
    state: Number(currentRound[1]),
    totalUSD: Number(formatUnits(currentRound[2] || "0", 18)),
    liveTime: Number(currentRound[3]),
    duration: Number(currentRound[4]),
    entriesCount: Number(currentRound[5]),
    minBetUSD: Number(formatUnits(currentRound[6] || "0", 18)),
    maxBetUSD: Number(formatUnits(currentRound[7] || "0", 18)),
  };
};

export const calculateWinner = async (account: string) => {
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "calculateWinner",
    account,
  });
  return tx;
};

export const enterPotWithInventoryItem = async (
  itemId: number,
  account: string
) => {
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "enterPotWithInventoryItem",
    args: [itemId],
    account,
  });

  return tx;
};

export const claimFees = async (account: string) => {
  const tx = await writeContractAndWait({
    address,
    abi,
    functionName: "claimFees",
    account,
  });

  return tx;
};
